/**
 * mobileTabsAccess — fail-closed Feature-Flag für die mobile Bottom-Tab-Bar
 * (M-UX-6.1, `plan-ux-6.1-mobile-tabs.md` §0/E0).
 *
 * EIGENES Flag (nicht `UX_SHELL_ENABLED` mitbenutzen), weil `UX_SHELL_ENABLED`
 * seit 2026-07-10 in Prod scharf ist: würde die Bar allein daran hängen, wäre
 * der Merge ein sofortiger, ungetesteter Mobile-Launch für ALLE Nutzer. Das
 * verletzt die Merge-Readiness-Disziplin. Deshalb ein separater, default-off
 * Schalter, dessen Flip (nach Design-Gate + mobilem E2E) der eigentliche Launch
 * ist.
 *
 * Firebase-frei (nur `process.env`) — importierbar aus `components/layout/*`
 * ohne Firebase-Zug (Muster `config/cockpitAccess.ts`/`config/unifiedInboxAccess.ts`).
 *
 * Null-Delta-Invariante: `UX_MOBILE_TABS_ENABLED !== true` ⇒ Mobile-UI
 * byte-identisch zu heute (Hamburger + Drawer unverändert, keine Bar, kein
 * CSS-Delta an Bestandsflächen).
 *
 * @module config/mobileTabsAccess
 */

/**
 * Allgemeines Feature-Flag für die mobile Bottom-Tab-Bar. Fail-closed: nur bei
 * explizitem `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load
 * ausgewertet (Muster `UX_COCKPIT_ENABLED`/`UNIFIED_INBOX_ENABLED`).
 */
export const UX_MOBILE_TABS_ENABLED: boolean =
  process.env.REACT_APP_UX_MOBILE_TABS_ENABLED === 'true';

/**
 * Beta-Erprobung mobile Tab-Bar — hartkodiert (Muster `RECEIPT_SCAN_BETA_UIDS`),
 * für die kontrollierte Live-Erprobung durch Test- (b3DRR1…) und Business-
 * Account (70Tgnu…). Vor allgemeiner Freigabe wieder leeren.
 */
export const MOBILE_TABS_BETA_UIDS: readonly string[] = [
  'b3DRR1Cj63cRjdJg5VCEUpBL7hk1',
  '70TgnuHwmDNI2LovagtcxXtIflo2',
];

/**
 * Entscheidet fail-closed, ob für einen Nutzer die mobile Bottom-Tab-Bar aktiv
 * ist. Ersetzt den direkten {@link UX_MOBILE_TABS_ENABLED}-Lesezugriff im
 * Konsumenten (`components/layout/MainLayout.tsx`) — die zusätzliche
 * `uxShellEnabled`- und `!isDesktop`-Kopplung bleibt beim Konsumenten (E1/E6),
 * diese Funktion trägt NUR den Flag-/Beta-Anteil.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das Flag aktiv
 *          ist. Sonst `false`.
 */
export function canUseMobileTabs(uid: string | null | undefined): boolean {
  if (uid && MOBILE_TABS_BETA_UIDS.includes(uid)) {
    return true;
  }
  return UX_MOBILE_TABS_ENABLED;
}
